import type {
  ComponentMaterial,
  PackagingComponentInput,
  RecyclingStream,
} from '@/shared/hooks/api/packagingSuppliers'
import { getAllowedRecyclingStreamsForMaterial } from './componentMaterialRecyclingStreams'
import type {
  ComponentDraft,
  ComponentEditorLabels,
} from './componentEditorTypes'
import {
  componentRecyclingPctInputSchema,
  getComponentRecyclingPctInputError,
} from './componentRecyclingPct'
import {
  componentWeightGramsInputSchema,
  getComponentWeightGramsInputError,
} from './componentWeightGrams'

export function createEmptyPackagingComponent(): ComponentDraft {
  return {
    name: '',
    material: null,
    weightInput: '',
    recyclable: false,
    recyclingStream: null,
    recyclingPctInput: '0',
    reusable: false,
    notes: null,
  }
}

export function buildMaterialOptions(labels: ComponentEditorLabels) {
  return (Object.keys(labels.materials) as ComponentMaterial[]).map(
    (value) => ({
      value,
      label: labels.materials[value],
    })
  )
}

export function buildRecyclingStreamOptionsForMaterial(
  labels: ComponentEditorLabels,
  material: ComponentMaterial | null
) {
  if (material === null) {
    return []
  }

  const allowedStreams = getAllowedRecyclingStreamsForMaterial(material)
  const streamOptions = allowedStreams.map((value) => ({
    value,
    label: labels.recyclingStreams[value],
  }))

  return [
    ...streamOptions,
    {
      value: 'NONE' as const,
      label: labels.recyclingStreams.NONE,
    },
  ]
}

export function getMaterialLabel(
  material: ComponentMaterial,
  labels: ComponentEditorLabels
) {
  return labels.materials[material] ?? material
}

export function getRecyclingStreamLabel(
  stream: RecyclingStream | null | undefined,
  labels: ComponentEditorLabels
) {
  if (!stream) {
    return labels.none
  }
  return labels.recyclingStreams[stream] ?? stream
}

export function formatBoolean(value: boolean, labels: ComponentEditorLabels) {
  return value ? labels.yes : labels.no
}

export function getWeightInputErrorMessage(
  weightInput: string,
  labels: ComponentEditorLabels
) {
  const error = getComponentWeightGramsInputError(weightInput)
  if (error === 'required') {
    return labels.weightGramsRequired
  }
  if (error === 'invalid') {
    return labels.weightGramsInvalid
  }
  return null
}

export function getRecyclingPctErrorMessage(
  recyclingPctInput: string,
  labels: ComponentEditorLabels
) {
  if (getComponentRecyclingPctInputError(recyclingPctInput) !== null) {
    return labels.recyclingPercentageInvalid
  }
  return null
}

function isRecyclingPctInputValid(recyclingPctInput: string) {
  return componentRecyclingPctInputSchema.safeParse(recyclingPctInput).success
}

export function isComponentValid(component: ComponentDraft) {
  const hasValidRecyclingPct =
    !component.recyclable || isRecyclingPctInputValid(component.recyclingPctInput)

  return (
    component.name.trim().length > 0 &&
    component.material !== null &&
    componentWeightGramsInputSchema.safeParse(component.weightInput).success &&
    hasValidRecyclingPct
  )
}

export function normalizeComponent(
  component: ComponentDraft
): PackagingComponentInput {
  if (!component.material) {
    throw new Error('Material is required')
  }
  const weightInGrams = componentWeightGramsInputSchema.parse(
    component.weightInput
  )
  const recyclingPct = componentRecyclingPctInputSchema.parse(
    component.recyclingPctInput
  )
  return {
    name: component.name.trim(),
    material: component.material,
    weightInGrams,
    recyclable: component.recyclable,
    recyclingStream: component.recyclingStream,
    recyclingPct,
    reusable: component.reusable,
    notes: component.notes?.trim() || null,
  }
}
